/**
 * Score, combo and the last judgement.
 *
 * Kept as DOM rather than drawn into the scene, for the same reason the song
 * info panel is: text belongs to the document, and the bloom pass that makes
 * the seals glow would smear a readout the player is meant to read quickly.
 *
 * The timing line is the part worth having. A grade alone tells you that you
 * were wrong; a signed offset tells you *which way*, which is the difference
 * between a player who improves and one who guesses.
 */

export interface JudgeDisplay {
  readonly grade: string;
  readonly timingOffsetMs: number;
  readonly timingDirection: 'early' | 'late' | 'exact';
  readonly tapped: boolean;
}

const STYLE_ID = 'mandalizm-judge-style';

const CSS = `
.mandalizm-judge {
  position: fixed; left: 14px; bottom: 14px; min-width: 208px;
  padding: 11px 13px; box-sizing: border-box;
  font: 12px/1.55 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfeff2; background: rgba(0, 8, 14, 0.62);
  border: 1px solid rgba(80, 230, 210, 0.2); border-radius: 8px;
  pointer-events: none; user-select: none;
}
.mandalizm-judge__score {
  font-size: 26px; line-height: 1.15; color: #eafeff; letter-spacing: 0.06em;
  font-variant-numeric: tabular-nums;
}
.mandalizm-judge__label { font-size: 10px; letter-spacing: 0.2em; color: rgba(140, 200, 215, 0.55); }
.mandalizm-judge__combo {
  font-size: 19px; color: #ffd98a; font-variant-numeric: tabular-nums; letter-spacing: 0.04em;
}
.mandalizm-judge__combo[data-idle="true"] { color: rgba(150, 200, 215, 0.4); }
.mandalizm-judge__grade {
  margin-top: 7px; font-size: 15px; letter-spacing: 0.16em;
  border-top: 1px solid rgba(80, 210, 200, 0.14); padding-top: 7px;
  transition: color 120ms ease;
}
.mandalizm-judge__timing { font-variant-numeric: tabular-nums; color: rgba(160, 220, 230, 0.8); }
.mandalizm-judge__counts { margin-top: 5px; font-size: 10.5px; color: rgba(140, 200, 215, 0.6); }
.mandalizm-judge--perfect { color: #ffe9a8; }
.mandalizm-judge--great   { color: #74ffbc; }
.mandalizm-judge--good    { color: #79bcff; }
.mandalizm-judge--bad     { color: #e08cf0; }
.mandalizm-judge--miss    { color: #ff7f92; }
@media (max-width: 600px), (max-height: 480px) {
  .mandalizm-judge--compact {
    left: max(12px, env(safe-area-inset-left)); bottom: calc(58px + env(safe-area-inset-bottom));
    width: min(320px, calc(100vw - 24px)); min-width: 0; padding: 8px 11px;
    display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas: "score-label combo-label" "score combo" "grade timing";
    column-gap: 10px; align-items: baseline;
  }
  .mandalizm-judge--compact[hidden] { display: none; }
  .mandalizm-judge--compact .mandalizm-judge__score-label { grid-area: score-label; }
  .mandalizm-judge--compact .mandalizm-judge__combo-label { grid-area: combo-label; }
  .mandalizm-judge--compact .mandalizm-judge__score { grid-area: score; font-size: 22px; }
  .mandalizm-judge--compact .mandalizm-judge__combo { grid-area: combo; font-size: 20px; }
  .mandalizm-judge--compact .mandalizm-judge__grade {
    grid-area: grade; margin-top: 4px; padding-top: 0; border-top: 0;
    font-size: 12px; letter-spacing: 0.08em;
  }
  .mandalizm-judge--compact .mandalizm-judge__timing {
    grid-area: timing; font-size: 9px; overflow-wrap: anywhere;
  }
  .mandalizm-judge--compact .mandalizm-judge__counts { display: none; }
}
`;

export class JudgePanel {
  readonly element: HTMLDivElement;

  private readonly score: HTMLDivElement;
  private readonly combo: HTMLDivElement;
  private readonly grade: HTMLDivElement;
  private readonly timing: HTMLDivElement;
  private readonly counts: HTMLDivElement;

  constructor(parent: HTMLElement = document.body) {
    injectStyle();
    this.element = document.createElement('div');
    this.element.className = 'mandalizm-judge';

    const scoreLabel = label('SCORE');
    scoreLabel.classList.add('mandalizm-judge__score-label');
    this.score = document.createElement('div');
    this.score.className = 'mandalizm-judge__score';
    this.score.textContent = '0';

    const comboLabel = label('COMBO');
    comboLabel.classList.add('mandalizm-judge__combo-label');
    this.combo = document.createElement('div');
    this.combo.className = 'mandalizm-judge__combo';
    this.combo.dataset['idle'] = 'true';
    this.combo.textContent = '0';

    this.grade = document.createElement('div');
    this.grade.className = 'mandalizm-judge__grade';
    this.grade.textContent = '—';

    this.timing = document.createElement('div');
    this.timing.className = 'mandalizm-judge__timing';
    this.timing.textContent = 'TIMING —';

    this.counts = document.createElement('div');
    this.counts.className = 'mandalizm-judge__counts';
    this.counts.textContent = '';

    this.element.append(
      scoreLabel, this.score,
      comboLabel, this.combo,
      this.grade, this.timing, this.counts,
    );
    parent.appendChild(this.element);
  }

  /**
   * Shows or hides the whole panel.
   *
   * Score and combo mean nothing on the title screen, and the counts line is
   * shorthand for somebody tuning the judge. Both belong to a run.
   */
  setVisible(visible: boolean): void {
    this.element.hidden = !visible;
  }

  /** Keeps the introductory demo HUD readable above controls on small screens. */
  setCompact(compact: boolean): void {
    this.element.classList.toggle('mandalizm-judge--compact', compact);
  }

  setScore(score: number, combo: number): void {
    this.score.textContent = score.toLocaleString();
    this.combo.textContent = String(combo);
    this.combo.dataset['idle'] = combo === 0 ? 'true' : 'false';
  }

  /**
   * Shows one judgement.
   *
   * A missed wave reports no offset: it was never tapped, so there is no "how
   * late" to quote, and printing the distance to a moment the player never
   * aimed at would be inventing a number.
   */
  setJudgement(judgement: JudgeDisplay | null): void {
    if (judgement === null) {
      this.grade.className = 'mandalizm-judge__grade';
      this.grade.textContent = '—';
      this.timing.textContent = 'TIMING —';
      return;
    }
    this.grade.className = `mandalizm-judge__grade mandalizm-judge--${judgement.grade}`;
    this.grade.textContent = judgement.grade.toUpperCase();
    if (!judgement.tapped) {
      this.timing.textContent = 'TIMING — (no input)';
      return;
    }
    const rounded = Math.round(judgement.timingOffsetMs);
    const sign = rounded > 0 ? '+' : rounded < 0 ? '' : '±';
    const direction =
      judgement.timingDirection === 'exact' ? '' : ` ${judgement.timingDirection.toUpperCase()}`;
    this.timing.textContent = `TIMING ${sign}${rounded} ms${direction}`;
  }

  /** Running tallies, including the early/late split for a later result screen. */
  setCounts(
    counts: Readonly<Record<string, number>>,
    early: number,
    late: number,
    maxCombo: number,
  ): void {
    this.counts.textContent =
      `P${counts['perfect'] ?? 0} G${counts['great'] ?? 0} ` +
      `g${counts['good'] ?? 0} B${counts['bad'] ?? 0} M${counts['miss'] ?? 0} · ` +
      `EARLY ${early} LATE ${late} · MAX ${maxCombo}`;
  }

  dispose(): void {
    this.element.remove();
  }
}

function label(text: string): HTMLDivElement {
  const element = document.createElement('div');
  element.className = 'mandalizm-judge__label';
  element.textContent = text;
  return element;
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
